import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Image,
  Button,
  Spinner,
  Tag,
  useToast,
  Checkbox,
  IconButton,
} from "@chakra-ui/react";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext";
import { DownloadIcon } from "@chakra-ui/icons";
import SendEmail from "../../components/SendEmail";

const LicensedVideoRequest = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [videos, setVideos] = useState([]);
  const [channels, setChannels] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChannels, setSelectedChannels] = useState({}); // { videoId: [channelId, ...] }
  const [requesting, setRequesting] = useState({}); // { videoId: true/false }
  const [showTerms, setShowTerms] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);

  // 데이터 불러오기
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 영상 목록
        const { data: videoData } = await supabase
          .from("shorts_licensed_videos")
          .select("*")
          .order("created_date", { ascending: false });

        setVideos(videoData || []);
        // 내 채널 목록
        const { data: channelData } = await supabase
          .from("shorts_channel")
          .select("*")
          .eq("user", user?.email);
        setChannels(channelData || []);
        // 내 신청 목록
        const { data: permissionData } = await supabase
          .from("shorts_permission_videos")
          .select("*")
          .eq("user", user?.email);
        setPermissions(permissionData || []);
      } finally {
        setLoading(false);
      }
    };
    if (user?.email) fetchData();
  }, [user?.email]);

  // 신청 상태 확인 함수
  const isAlreadyRequested = (videoId, channelId) => {
    return permissions.some(
      (p) =>
        (typeof p.licensed_video_ids === "string"
          ? p.licensed_video_ids.split(",").includes(String(videoId))
          : Array.isArray(p.licensed_video_ids)
          ? p.licensed_video_ids.includes(String(videoId))
          : false) &&
        (typeof p.channel_ids === "string"
          ? p.channel_ids
              .split(",")
              .map((id) => id.trim())
              .includes(String(channelId))
          : Array.isArray(p.channel_ids)
          ? p.channel_ids.includes(String(channelId))
          : false) &&
        ["Approved", "Pending"].includes(p.permission_status)
    );
  };

  // 신청하기 버튼 클릭
  const handleRequest = async (videoId) => {
    const channelIds = selectedChannels[videoId] || [];
    // 이미 신청된 채널은 제외하고, 추가 신청만 insert
    const newChannelIds = channelIds.filter(
      (chId) => !isAlreadyRequested(videoId, chId)
    );
    if (!newChannelIds.length) return;
    setRequesting((prev) => ({ ...prev, [videoId]: true }));
    try {
      const inserts = newChannelIds.map((channelId) => ({
        user: user.email,
        licensed_video_ids: videoId,
        channel_ids: channelId,
        permission_status: "Pending",
        agree_terms: true,
        additional_channel: true,
      }));
      const { error } = await supabase
        .from("shorts_permission_videos")
        .insert(inserts);
      if (error) throw error;

      // 선택된 비디오 정보 가져오기
      const selectedVideo = videos.find((v) => v.id === videoId);
      const selectedChannelsInfo = channels.filter((ch) =>
        newChannelIds.includes(ch.id)
      );

      // 이메일 내용 구성
      const emailContent = `
        <p>영상 라이센스 신청이 접수되었습니다.</p>
        <p>영상 정보:</p>
        <p>- 제목: ${selectedVideo.title}</p>
        <p>- 국가: ${selectedVideo.nation}</p>
        <p>- OTT: ${selectedVideo.ott || "없음"}</p>
        <p>- 개봉연도: ${selectedVideo.published_year}</p>
        <p>- 장르: ${selectedVideo.genre}</p>
        <p>- 감독: ${selectedVideo.director}</p>
        <p>- 출연: ${selectedVideo.cast}</p>

        <p>신청 채널:</p>
        ${selectedChannelsInfo
          .map((ch) => `<p>- ${ch.channel_title || ch.id}</p>`)
          .join("")}

        <p>신청자: ${user.email}</p>
        <p>신청일시: ${new Date().toLocaleString("ko-KR")}</p>
      `.trim();

      await SendEmail({
        email: "contact@plpl.kr",
        ccEmails: user?.email ? [user.email] : [],
        subject: `[신청] 영상 라이센스 신청 / ${user?.email}`,
        message: emailContent,
      });

      toast({
        title: "신청이 완료되었습니다.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      // 신청 목록 새로고침
      const { data: permissionData } = await supabase
        .from("shorts_permission_videos")
        .select("*")
        .eq("user", user.email);
      setPermissions(permissionData || []);
      setSelectedChannels((prev) => ({ ...prev, [videoId]: [] }));
    } catch (e) {
      toast({
        title: "신청에 실패했습니다.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setRequesting((prev) => ({ ...prev, [videoId]: false }));
    }
  };

  if (loading) {
    return (
      <Flex minH="60vh" align="center" justify="center">
        <Spinner size="xl" color="teal.400" />
      </Flex>
    );
  }

  return (
    <Box maxW="900px" mx="auto" py={8}>
      <Text fontSize="2xl" fontWeight="bold" mb={6}>
        영상 라이센스 신청하기
      </Text>
      <Text mb={2} color="gray.700" fontSize="sm">
        ※ 영상 라이선스 승인은 매주 목요일까지 접수 후, 그 다음주 화요일까지
        처리되며 지연이 발생될 수 있습니다.
      </Text>
      {/* 약관 동의 안내 및 토글 */}
      <Box mb={6} mt={4} bg="white" borderRadius="xl" boxShadow="md" p={5}>
        <Flex align="center" justify="space-between">
          <Flex align="center" gap={2}>
            <Checkbox
              isChecked={isAgreed}
              onChange={(e) => setIsAgreed(e.target.checked)}
              colorScheme="green"
              size="lg"
            />
            <Text fontWeight="semibold">영상 사용 약관에 동의하였습니다.</Text>
          </Flex>
          <Button
            size="sm"
            variant="ghost"
            colorScheme="teal"
            onClick={() => setShowTerms((prev) => !prev)}
          >
            {showTerms ? "닫기" : "상세보기"}
          </Button>
        </Flex>
        {showTerms && (
          <Box mt={4} bg="gray.50" borderRadius="lg" p={5} fontSize="sm">
            <Box as="ul" pl={0} mb={2}>
              <Box as="li" mb={1}>
                <Box as="span" color="green.400">
                  ✅
                </Box>{" "}
                채널은 레이블리 for 쇼츠와 계약 체결 완료 및 등록한 채널에 한 해
                승인됩니다.
              </Box>
              <Box as="li" mb={1}>
                <Box as="span" color="green.400">
                  ✅
                </Box>{" "}
                레이블리 for 쇼츠를 통해 제공되는 모든 콘텐츠는 각 권리자의 동의
                하에 제공됩니다.
              </Box>
              <Box as="li" mb={1}>
                <Box as="span" color="green.400">
                  ✅
                </Box>{" "}
                각 영상마다 채널 별로 모두 사용 신청 및 승인이 필요합니다.
              </Box>
              <Box as="li" mb={1}>
                <Box as="span" color="green.400">
                  ✅
                </Box>{" "}
                승인이 완료 되면, 해당 채널은 화이트리스트 적용됩니다.
              </Box>
              <Box as="li" mb={1}>
                <Box as="span" color="green.400">
                  ✅
                </Box>{" "}
                제작 된 쇼츠와 관련하여 민원 발생 또는 사회적 논란의 여지가
                있거나 야기될 경우, 쇼츠 교체 혹은 삭제해야 합니다.
              </Box>
              <Box as="li" mb={1}>
                <Box as="span" color="green.400">
                  ✅
                </Box>{" "}
                채널 운영자는 영상을 활용한 쇼츠를 제작하기 위한 인/허가를
                취득하고 관련 법령을 준수해야하며, 이와 관련한 법적 분쟁이
                발생할 경우 채널 운영자의 비용과 책임으로 해결하고 "레이블리"와
                "영상 권리자"를 면책시켜야 합니다.
              </Box>
              <Box as="li" mb={1}>
                <Box as="span" color="red.400">
                  🚫
                </Box>{" "}
                영상의 결말 혹은 스포일러는 금지됩니다.
              </Box>
              <Box as="li" mb={1}>
                <Box as="span" color="red.400">
                  🚫
                </Box>{" "}
                쇼츠가 아닌 롱폼 제작은 금지됩니다.
              </Box>
              <Box as="li" mb={1}>
                <Box as="span" color="red.400">
                  🚫
                </Box>{" "}
                유튜브 외 플랫폼(틱톡, 인스타그램 등)에서는 사용할 수 없습니다.
              </Box>
              <Box as="li" mb={1}>
                <Box as="span" color="red.400">
                  🚫
                </Box>{" "}
                영상의 원본 및 자막 파일은 쇼츠 제작 외 사용, 편집, 배포 및
                공유가 금지됩니다.
              </Box>
              <Box as="li" mb={1}>
                <Box as="span" color="red.400">
                  🚫
                </Box>{" "}
                계약 미체결 및 승인되지 않은 채널에서 사용으로 인한 저작권 침해
                및 법적 책임을 물을 수 있습니다.
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      <Flex direction="column" gap={8}>
        {videos.map((video) => {
          // 내 채널 중 하나라도 해당 영상 라이선스가 있으면 true
          const hasLicensedChannel = channels.some((ch) =>
            permissions.some(
              (p) =>
                (typeof p.licensed_video_ids === "string"
                  ? p.licensed_video_ids.split(",").includes(String(video.id))
                  : Array.isArray(p.licensed_video_ids)
                  ? p.licensed_video_ids.includes(String(video.id))
                  : false) &&
                (typeof p.channel_ids === "string"
                  ? p.channel_ids
                      .split(",")
                      .map((id) => id.trim())
                      .includes(String(ch.id))
                  : Array.isArray(p.channel_ids)
                  ? p.channel_ids.includes(String(ch.id))
                  : false) &&
                ["Approved", "Pending"].includes(p.permission_status)
            )
          );
          return (
            <Box
              key={video.id}
              bg="white"
              borderRadius="xl"
              boxShadow="md"
              p={6}
              display="flex"
              gap={6}
              alignItems="flex-start"
              position="relative"
            >
              <Image
                src={video.thumbnail}
                alt={video.title}
                w="180px"
                h="220px"
                objectFit="contain"
                borderRadius="md"
                mr={4}
                cursor="pointer"
                _hover={{ opacity: 0.8 }}
                onClick={() =>
                  video.video_ott_link &&
                  window.open(video.video_ott_link, "_blank")
                }
              />
              {hasLicensedChannel &&
                video.download_link &&
                permissions.some(
                  (p) =>
                    (typeof p.licensed_video_ids === "string"
                      ? p.licensed_video_ids
                          .split(",")
                          .includes(String(video.id))
                      : Array.isArray(p.licensed_video_ids)
                      ? p.licensed_video_ids.includes(String(video.id))
                      : false) && p.permission_status === "Approved"
                ) && (
                  <IconButton
                    icon={<DownloadIcon />}
                    aria-label="파일 다운로드"
                    position="absolute"
                    top={6}
                    right={6}
                    size="sm"
                    colorScheme="teal"
                    variant="ghost"
                    onClick={() => window.open(video.download_link, "_blank")}
                  />
                )}
              <Box flex={1}>
                <Text fontWeight="bold" fontSize="xl" mb={1}>
                  {video.title}
                  {"  "}
                  <Text as="span" fontSize="sm" color="gray.500">
                    {video.nation}{" "}
                    {video.ott && (
                      <span style={{ fontSize: 16, color: "#888" }}>
                        ({video.ott})
                      </span>
                    )}
                  </Text>
                </Text>
                <Text color="gray.600" fontSize="sm" mb={1}>
                  개봉연도 {video.published_year} | 장르 {video.genre} | 감독{" "}
                  {video.director}
                </Text>
                <Text color="gray.600" fontSize="sm" mb={1}>
                  출연 {video.cast}
                </Text>
                <Text color="gray.700" fontSize="md" mb={2}>
                  {video.introduction}
                </Text>
                {/* 태그/가이드/기타 정보 등 필요시 추가 */}
                <Flex gap={2} wrap="wrap" mb={2}>
                  {video.guide && (
                    <Text
                      color="#38b2ac"
                      fontSize="sm"
                      whiteSpace="pre-line"
                      mb={1}
                    >
                      {video.guide}
                    </Text>
                  )}
                </Flex>
                {video.comments && (
                  <Text
                    color="#3182ce"
                    fontSize="sm"
                    mb={2}
                    whiteSpace="pre-line"
                  >
                    {video.comments}
                  </Text>
                )}
                {video.ott === "shortcha" && (
                  <Text
                    color="red.500"
                    fontWeight="bold"
                    fontSize="sm"
                    mb={2}
                    cursor="pointer"
                    _hover={{ textDecoration: "underline" }}
                    onClick={() =>
                      window.open(
                        "https://docs.google.com/forms/d/e/1FAIpQLSeh2Tv94pdDFPOKr883Lc4qz-67SzhCga7ZFTDRAVQP9IMqbQ/viewform",
                        "_blank"
                      )
                    }
                  >
                    &lt;쇼츠 URL 필수 제출&gt; 클릭하여 링크 제출하기
                    <br />
                    URL로 제출되지 않을 경우, 추후 저작권 제재 대상이 될 수
                    있습니다.
                  </Text>
                )}
                {/* 채널별 신청 UI */}
                <Box mt={2}>
                  <Box bg="white" borderRadius="lg" p={3}>
                    <Flex gap={2} wrap="wrap">
                      {channels.map((ch) => {
                        const already = isAlreadyRequested(video.id, ch.id);
                        const selected = (
                          selectedChannels[video.id] || []
                        ).includes(ch.id);
                        let bg = "#e1e2e3"; // 미신청+미선택: 연회색
                        let color = "#38b2ac"; // 미신청+미선택: 연한 민트
                        if (already) {
                          if (
                            permissions.find(
                              (p) =>
                                (typeof p.licensed_video_ids === "string"
                                  ? p.licensed_video_ids
                                      .split(",")
                                      .includes(String(video.id))
                                  : Array.isArray(p.licensed_video_ids)
                                  ? p.licensed_video_ids.includes(
                                      String(video.id)
                                    )
                                  : false) &&
                                (typeof p.channel_ids === "string"
                                  ? p.channel_ids
                                      .split(",")
                                      .includes(String(ch.id))
                                  : Array.isArray(p.channel_ids)
                                  ? p.channel_ids.includes(String(ch.id))
                                  : false) &&
                                p.permission_status === "Approved"
                            )
                          ) {
                            bg = "#4299e1"; // Approved: 파랑
                          } else {
                            bg = "#9AE6B4"; // Pending: 밝은 초록색
                          }
                          color = "white";
                        } else if (selected) {
                          bg = "#2ec4b6"; // 선택된 진한 민트
                          color = "white";
                        }
                        return (
                          <Box
                            key={ch.id}
                            borderRadius="lg"
                            bg={bg}
                            color={color}
                            px={2}
                            py={1}
                            cursor={already ? "not-allowed" : "pointer"}
                            minW="72px"
                            minH="32px"
                            mb={1}
                            mr={1}
                            fontWeight={already ? "normal" : "bold"}
                            fontSize="xs"
                            boxShadow={selected ? "md" : "none"}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            textAlign="center"
                            position="relative"
                            transition="all 0.15s"
                            onClick={() => {
                              if (already) return;
                              setSelectedChannels((prev) => {
                                const prevArr = prev[video.id] || [];
                                if (prevArr.includes(ch.id)) {
                                  // 해제
                                  return {
                                    ...prev,
                                    [video.id]: prevArr.filter(
                                      (id) => id !== ch.id
                                    ),
                                  };
                                } else {
                                  // 선택
                                  return {
                                    ...prev,
                                    [video.id]: [...prevArr, ch.id],
                                  };
                                }
                              });
                            }}
                          >
                            {/* 체크박스는 숨김 */}
                            <Checkbox
                              value={ch.id}
                              isChecked={selected}
                              isDisabled={already}
                              display="none"
                            />
                            {ch.channel_title || ch.id}
                          </Box>
                        );
                      })}
                    </Flex>
                  </Box>
                  <Button
                    colorScheme="blue"
                    size="sm"
                    onClick={() => handleRequest(video.id)}
                    isDisabled={
                      !(selectedChannels[video.id] || []).filter(
                        (chId) => !isAlreadyRequested(video.id, chId)
                      ).length ||
                      requesting[video.id] ||
                      !isAgreed
                    }
                    mt={2}
                  >
                    신청하기
                  </Button>
                  {/* 내 신청 목록 표시 */}
                  <Box mt={2}>
                    {permissions
                      .filter(
                        (p) =>
                          (typeof p.licensed_video_ids === "string"
                            ? p.licensed_video_ids
                                .split(",")
                                .map((id) => id.trim())
                                .includes(String(video.id))
                            : Array.isArray(p.licensed_video_ids)
                            ? p.licensed_video_ids.includes(String(video.id))
                            : false) &&
                          ["Approved", "Pending"].includes(
                            p.permission_status
                          ) &&
                          channels.some((ch) =>
                            typeof p.channel_ids === "string"
                              ? p.channel_ids
                                  .split(",")
                                  .map((id) => id.trim())
                                  .includes(String(ch.id))
                              : Array.isArray(p.channel_ids)
                              ? p.channel_ids.includes(String(ch.id))
                              : false
                          )
                      )
                      .map((p, idx) => {
                        const ch = channels.find((c) =>
                          typeof p.channel_ids === "string"
                            ? p.channel_ids
                                .split(",")
                                .map((id) => id.trim())
                                .includes(String(c.id))
                            : Array.isArray(p.channel_ids)
                            ? p.channel_ids.includes(String(c.id))
                            : false
                        );
                        return ch ? (
                          <Tag key={ch.id} colorScheme="teal" mr={2} mb={1}>
                            {ch.channel_title || ch.id}
                          </Tag>
                        ) : null;
                      })}
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Flex>
    </Box>
  );
};

export default LicensedVideoRequest;
